/* TeamScreen (DESIGN40) — ROOT ONLY. The web Root panel's mini-app story on
   a phone: who is on the team, what each account may do in the mini app,
   whether it is linked to Telegram, and the two actions that matter here —
   invite (with Telegram's own «send to…» sheet) and unlink.

   Rights are the same bundles the web grants: Architect access opens the
   app; Generator / Estimator gate the tabs and the approvals. Root accounts
   hold everything implicitly and are never toggled.

   «Add teammate» creates a Telegram-only account: a throwaway password the
   screen never shows, the chosen rights, and the invite in one go — the
   person taps the link in Telegram and is in. Bot setup itself stays in the
   web Root panel: the mini app cannot exist before the bot does. */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fmtDay } from '../../architect/utils';
import { useAuth } from '../../auth/AuthContext';
import { AdminUser, createUser, listUsers, setUserDisabled, setUserPermissions } from '../../auth/api';
import { Sheet } from '../components/Sheet';
import { getWebApp, haptic, showConfirm, useBackButton, useMainButton } from '../telegram';
import {
  MiniState, PERM_ESTIMATE, PERM_FEEDBACK, PERM_GENERATE, PERM_PRICING, PERM_USE, getLinks, issueInvite,
  miniStates, randomPassword, shareUrl, tmaErrorText, unlink,
} from '../tmaAdmin';

interface Props {
  onBack: () => void;
}

type Badge = { kind: 'root' | 'architect' | 'manager' | 'developer'; label: string };

function badgeOf(u: AdminUser): Badge {
  if (u.isRoot) return { kind: 'root', label: 'Root' };
  const p = u.permissions || [];
  if (p.includes(PERM_PRICING) && !p.includes(PERM_GENERATE) && !p.includes(PERM_ESTIMATE)) return { kind: 'architect', label: 'Pricing' };
  if (p.includes(PERM_USE)) return { kind: 'architect', label: 'Architect' };
  if (p.some((x) => PERM_FEEDBACK.includes(x)) && p.length <= 2) return { kind: 'manager', label: 'Manager' };
  return { kind: 'developer', label: 'Developer' };
}

const fmtWhen = (iso: string | null | undefined) => fmtDay(iso);

const INVITE_TEXT = 'Your LATO Architect access — open this link in Telegram:';

export default function TeamScreen({ onBack }: Props) {
  const { user: me } = useAuth();
  useBackButton(true, onBack);

  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [mini, setMini] = useState<Map<string, MiniState>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // The invite sheet — one link, shown once, shared through Telegram.
  const [invite, setInvite] = useState<{ user: AdminUser; link: string; expiresAt: string | null } | null>(null);
  const [copied, setCopied] = useState(false);

  // «Add teammate» sheet.
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [gen, setGen] = useState(true);
  const [est, setEst] = useState(false);
  const [price, setPrice] = useState(false);

  const load = useCallback(async () => {
    try {
      const [list, links] = await Promise.all([listUsers(), getLinks().catch(() => ({} as any))]);
      setUsers(list);
      setMini(miniStates(links));
      setError(null);
    } catch (err) {
      setError(tmaErrorText(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = useCallback(async (u: AdminUser, bundle: string[], grant: boolean) => {
    haptic.impact('light');
    setBusyId(u.id);
    try {
      const r = await setUserPermissions(u.id, grant ? { grant: bundle } : { revoke: bundle });
      setUsers((prev) => (prev || []).map((x) => (x.id === u.id ? { ...x, permissions: r.permissions } : x)));
    } catch (err) {
      haptic.notify('error');
      setError(tmaErrorText(err));
    } finally {
      setBusyId(null);
    }
  }, []);

  const doInvite = useCallback(async (u: AdminUser) => {
    haptic.impact('medium');
    setBusyId(u.id);
    setCopied(false);
    try {
      const r = await issueInvite(u.id);
      setInvite({ user: u, link: r.link, expiresAt: r.expires_at ?? null });
      setMini((prev) => new Map(prev).set(u.id, { kind: 'invited', expiresAt: r.expires_at ?? null }));
      haptic.notify('success');
    } catch (err) {
      haptic.notify('error');
      setError(tmaErrorText(err));
    } finally {
      setBusyId(null);
    }
  }, []);

  const doUnlink = useCallback(async (u: AdminUser) => {
    const ok = await showConfirm(
      `Unlink ${u.displayName || u.username} from the mini app? Their sessions end now; a new invite re-admits them.`,
    );
    if (!ok) return;
    setBusyId(u.id);
    try {
      await unlink(u.id);
      setMini((prev) => {
        const next = new Map(prev);
        next.delete(u.id);
        return next;
      });
      haptic.notify('success');
    } catch (err) {
      haptic.notify('error');
      setError(tmaErrorText(err));
    } finally {
      setBusyId(null);
    }
  }, []);

  const setBlocked = useCallback(async (u: AdminUser, disabled: boolean) => {
    if (disabled) {
      const ok = await showConfirm(
        `Disable ${u.displayName || u.username}? Every session ends now — web and mini app — and their invites stop working. You can enable them again any time.`,
      );
      if (!ok) return;
    }
    setBusyId(u.id);
    try {
      const r = await setUserDisabled(u.id, disabled);
      setUsers((prev) => (prev || []).map((x) => (x.id === u.id ? { ...x, isDisabled: r.user.isDisabled } : x)));
      haptic.notify('success');
    } catch (err) {
      haptic.notify('error');
      setError(tmaErrorText(err));
    } finally {
      setBusyId(null);
    }
  }, []);

  const share = useCallback(() => {
    if (!invite) return;
    haptic.impact('light');
    const app = getWebApp();
    const url = shareUrl(invite.link, INVITE_TEXT);
    if (app && app.openTelegramLink) app.openTelegramLink(url);
    else window.open(url, '_blank', 'noopener');
  }, [invite]);

  const copy = useCallback(async () => {
    if (!invite) return;
    try {
      await navigator.clipboard.writeText(invite.link);
      setCopied(true);
      haptic.notify('success');
    } catch {
      setCopied(false);
    }
  }, [invite]);

  const addValid = name.trim().length > 0 && /^[a-z0-9._-]{3,}$/i.test(username.trim()) && /\S+@\S+\.\S+/.test(email.trim());

  const addTeammate = useCallback(async () => {
    if (!addValid || adding) return;
    setAdding(true);
    setAddError(null);
    try {
      const perms = [PERM_USE].concat(gen ? [PERM_GENERATE] : [], est ? [PERM_ESTIMATE] : [], price ? [PERM_PRICING] : []);
      const r = await createUser({
        username: username.trim(),
        email: email.trim(),
        displayName: name.trim(),
        password: randomPassword(),
        permissions: perms,
      });
      const created: AdminUser = { ...r.user, permissions: r.permissions };
      setUsers((prev) => [created].concat(prev || []));
      const inv = await issueInvite(created.id);
      setMini((prev) => new Map(prev).set(created.id, { kind: 'invited', expiresAt: inv.expires_at ?? null }));
      setAddOpen(false);
      setName(''); setUsername(''); setEmail(''); setGen(true); setEst(false); setPrice(false);
      setCopied(false);
      setInvite({ user: created, link: inv.link, expiresAt: inv.expires_at ?? null });
      haptic.notify('success');
    } catch (err) {
      haptic.notify('error');
      setAddError(tmaErrorText(err));
    } finally {
      setAdding(false);
    }
  }, [addValid, adding, gen, est, price, username, email, name]);

  /* The single MainButton: «Add teammate» on the list, «Create & invite»
     inside the add sheet, hidden while the invite sheet shows its own. */
  useMainButton(
    invite
      ? { text: 'Share in Telegram', visible: true, enabled: true, onClick: share }
      : addOpen
        ? { text: adding ? 'Creating…' : 'Create & invite', visible: true, enabled: addValid && !adding, progress: adding, onClick: addTeammate }
        : { text: 'Add teammate', visible: true, enabled: true, onClick: () => { haptic.impact('light'); setAddOpen(true); } },
  );

  const sorted = useMemo(
    () => (users || []).slice().sort((a, b) => {
      if (a.isRoot !== b.isRoot) return a.isRoot ? -1 : 1;
      return (a.displayName || a.username).localeCompare(b.displayName || b.username);
    }),
    [users],
  );

  return (
    <div className="tma-screen tma-team">
      <div className="tma-projects-inner">
        <h1 className="tma-title">Team</h1>
        <p className="tma-team-lead">
          Manage access and invite teammates through Telegram.
        </p>

        {error ? <div className="tma-banner err" role="alert">{error}</div> : null}

        {users === null && error ? (
          <div className="tma-empty" role="status"><div className="tma-empty-title">Team could not load</div><div className="tma-empty-sub">Check your connection and try again.</div><button type="button" className="tma-btn tma-btn-primary" onClick={() => void load()}>Try again</button></div>
        ) : users === null ? (
          <div className="tma-cards">
            {[0, 1, 2].map((i) => (
              <div className="tma-card tma-card-skel" key={i}>
                <div className="tma-skel tma-skel-name" />
                <div className="tma-skel tma-skel-meta" />
              </div>
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="tma-empty"><div className="tma-empty-title">No teammates yet</div><div className="tma-empty-sub">Add someone to give them access to the mini app.</div></div>
        ) : (
          <div className="tma-cards">
            {sorted.map((u) => {
              const badge = badgeOf(u);
              const st: MiniState = mini.get(u.id) || { kind: 'none' };
              const perms = u.permissions || [];
              const hasUse = u.isRoot || perms.includes(PERM_USE);
              const busy = busyId === u.id;
              const isMe = me?.id === u.id;
              return (
                <div className={'tma-card tma-member' + (u.isDisabled ? ' off' : '')} key={u.id}>
                  <div className="tma-member-top">
                    <div className="tma-member-who">
                      <span className="tma-member-name">
                        {u.displayName || u.username}
                        {isMe ? <span className="tma-member-me"> · you</span> : null}
                      </span>
                      <span className="tma-member-sub">@{u.username}</span>
                    </div>
                    <span className={'tma-role b-' + badge.kind}>{badge.label}</span>
                  </div>

                  <div className="tma-member-link">
                    {u.isDisabled ? <span className="tma-link-state disabled">Account disabled</span> : null}
                    {st.kind === 'linked' ? (
                      <span className="tma-link-state linked">
                        Linked to Telegram{st.telegramUsername ? ' · @' + st.telegramUsername : ''}
                        {st.linkedAt ? ' · ' + fmtWhen(st.linkedAt) : ''}
                      </span>
                    ) : st.kind === 'invited' ? (
                      <span className="tma-link-state invited">
                        Invite pending{st.expiresAt ? ' · until ' + fmtWhen(st.expiresAt) : ''}
                      </span>
                    ) : (
                      <span className="tma-link-state none">Not linked to Telegram</span>
                    )}
                  </div>

                  {u.isRoot ? (
                    <div className="tma-member-rights-root">Every right in the mini app, implicitly.</div>
                  ) : (
                    <div className="tma-member-permissions">
                    <div className="tma-member-section-label">Mini app access</div>
                    <div className="tma-rights" aria-label="Mini app rights">
                      <button
                        type="button"
                        className={'tma-right' + (hasUse ? ' on' : '')}
                        disabled={busy}
                        aria-pressed={hasUse}
                        onClick={() => toggle(u, [PERM_USE], !hasUse)}
                      >
                        Architect access
                      </button>
                      <button
                        type="button"
                        className={'tma-right' + (perms.includes(PERM_GENERATE) ? ' on' : '')}
                        disabled={busy || !hasUse}
                        aria-pressed={perms.includes(PERM_GENERATE)}
                        title={hasUse ? 'Poster, estimate request, proposal — and their approvals' : 'Grant Architect access first'}
                        onClick={() => toggle(u, [PERM_GENERATE], !perms.includes(PERM_GENERATE))}
                      >
                        Generator
                      </button>
                      <button
                        type="button"
                        className={'tma-right' + (perms.includes(PERM_ESTIMATE) ? ' on' : '')}
                        disabled={busy || !hasUse}
                        aria-pressed={perms.includes(PERM_ESTIMATE)}
                        title={hasUse ? 'Estimates — editing and approving' : 'Grant Architect access first'}
                        onClick={() => toggle(u, [PERM_ESTIMATE], !perms.includes(PERM_ESTIMATE))}
                      >
                        Estimator
                      </button>
                      <button type="button" className={'tma-right' + (perms.includes(PERM_PRICING) ? ' on' : '')}
                        disabled={busy || !hasUse} aria-pressed={perms.includes(PERM_PRICING)}
                        title={hasUse ? 'Pricing — editing and assigned approvals' : 'Grant Architect access first'}
                        onClick={() => toggle(u, [PERM_PRICING], !perms.includes(PERM_PRICING))}>Pricing</button>
                    </div>
                    </div>
                  )}

                  <div className="tma-member-actions">
                    {st.kind === 'linked' ? (
                      <>
                        <button type="button" className="tma-btn tma-btn-ghost tma-btn-sm" disabled={busy || u.isDisabled} onClick={() => doInvite(u)}>
                          New link
                        </button>
                        {!isMe ? (
                          <button type="button" className="tma-btn tma-btn-danger tma-btn-sm" disabled={busy} onClick={() => doUnlink(u)}>
                            Unlink
                          </button>
                        ) : null}
                      </>
                    ) : (
                      <button
                        type="button"
                        className="tma-btn tma-btn-primary tma-btn-sm"
                        disabled={busy || !hasUse || u.isDisabled}
                        title={hasUse ? undefined : 'Grant Architect access first'}
                        onClick={() => doInvite(u)}
                      >
                        {st.kind === 'invited' ? 'Re-invite' : 'Invite'}
                      </button>
                    )}
                    {!isMe ? (
                      <button
                        type="button"
                        className={'tma-btn tma-btn-sm ' + (u.isDisabled ? 'tma-btn-ghost' : 'tma-btn-danger')}
                        disabled={busy}
                        onClick={() => setBlocked(u, !u.isDisabled)}
                      >
                        {u.isDisabled ? 'Enable' : 'Disable'}
                      </button>
                    ) : null}
                    {busy ? <span className="tma-spin tma-member-spin" /> : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="tma-mb-spacer" />
      </div>

      {/* ── invite link sheet ── */}
      <Sheet open={!!invite} onClose={() => setInvite(null)} title="Invite link">
        {invite ? (
          <div className="tma-invite">
            <div className="tma-invite-who">
              For <b>{invite.user.displayName || invite.user.username}</b>
              {invite.expiresAt ? ' · valid until ' + fmtWhen(invite.expiresAt) : ''}
            </div>
            <div className="tma-invite-link" role="textbox" aria-readonly="true">{invite.link}</div>
            <p className="tma-field-hint">
              Shown once. Whoever opens it in Telegram becomes this account — send it to that person only.
            </p>
            <div className="tma-invite-actions">
              <button type="button" className="tma-btn tma-btn-primary tma-btn-block" onClick={share}>
                Share in Telegram
              </button>
              <button type="button" className="tma-btn tma-btn-ghost tma-btn-block" onClick={copy}>
                {copied ? 'Copied ✓' : 'Copy link'}
              </button>
            </div>
          </div>
        ) : null}
      </Sheet>

      {/* ── add teammate sheet ── */}
      <Sheet open={addOpen} onClose={() => { if (!adding) setAddOpen(false); }} title="Add teammate">
        <div className="tma-add">
          <label className="tma-field-label" htmlFor="tma-teammate-name">Name</label>
          <input id="tma-teammate-name" className="tma-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Dana Black" autoComplete="off" />
          <label className="tma-field-label" htmlFor="tma-teammate-username">Username</label>
          <input id="tma-teammate-username" className="tma-input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="dana" autoCapitalize="none" autoCorrect="off" autoComplete="off" />
          <label className="tma-field-label" htmlFor="tma-teammate-email">Email</label>
          <input id="tma-teammate-email" className="tma-input" type="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="dana@company.com" autoCapitalize="none" autoComplete="off" />
          <label className="tma-field-label">Rights in the mini app</label>
          <div className="tma-rights">
            <span className="tma-right on static">Architect access</span>
            <button type="button" className={'tma-right' + (gen ? ' on' : '')} aria-pressed={gen} onClick={() => { haptic.impact('light'); setGen(!gen); }}>Generator</button>
            <button type="button" className={'tma-right' + (est ? ' on' : '')} aria-pressed={est} onClick={() => { haptic.impact('light'); setEst(!est); }}>Estimator</button>
            <button type="button" className={'tma-right' + (price ? ' on' : '')} aria-pressed={price} onClick={() => { haptic.impact('light'); setPrice(!price); }}>Pricing</button>
          </div>
          <p className="tma-field-hint">
            Signs in through Telegram only — no password to hand over. The invite link is created right after the account.
          </p>
          {addError ? <div className="tma-banner err" role="alert">{addError}</div> : null}
        </div>
      </Sheet>
    </div>
  );
}
